import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Calendar, MapPin, Clock, Heart, ExternalLink } from 'lucide-react';
import { Button } from '../components/ui/button';
import SectionHeading from '../components/SectionHeading';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const defaultFlyers = [
  {
    id: 'place-to-worship',
    title: "Let's Secure a Place to Worship",
    description: 'TNMC is seeking to secure a permanent place of worship in South Barrie. Our goal is $6,000/month to cover rent and expenses. Every dollar counts — become a monthly supporter and earn Sadaqah Jariyyah, a reward that never ends.',
    date: 'Ongoing Campaign',
    time: 'Monthly Support Needed',
    location: 'South Barrie, ON',
    image: '/flyers/place-to-worship.jpg',
    category: 'Fundraising',
    featured: true,
  },
  {
    id: 'community-potluck-aug',
    title: 'Community Potluck',
    description: 'Eat Together • Connect • Build Together. Bring your favorite dish to share with the community. Together, we are stronger. Everyone is welcome!',
    date: 'Saturday, August 1st, 2026',
    time: '5:30 PM - 8:30 PM (Asr 5:30 PM, Maghrib 8:50 PM)',
    location: 'St. Paul, 54 St. Paul Crescent, Barrie, ON',
    image: '/flyers/community-potluck.jpg',
    category: 'Community Event',
  },
  {
    id: 'movie-night-jun',
    title: 'Family Movie Night — Time Hoppers: The Silk Road',
    description: 'Bring your family to enjoy a Time together. FREE admission. Snacks available for purchase (starting at $5) to support TNMC programs. Adult supervision required — no outside food or drinks allowed.',
    date: 'Saturday, June 27, 2026',
    time: 'Movie: 6:30 PM (Asr 5:30 PM, Magrib 9:05 PM)',
    location: 'St. Paul, 54 St. Paul Crescent, Barrie, ON',
    image: '/flyers/movie-night.jpg',
    category: 'Family Event',
  },
  {
    id: 'eid-ul-adha',
    title: 'Eid ul Adha 2026',
    description: 'Join us for Eid ul Adha prayer! Takbeerat starts at 8:00 AM, Salat at 8:30 AM. Bring your prayer mat, shoe bags will be provided. Parking available on site and at Barrie South GO Station & Metro.',
    date: 'Date To Be Announced (TBA)',
    time: 'Takbeerat: 8:00 AM | Salat: 8:30 AM',
    location: '54 St. Paul Crescent, Barrie, ON',
    image: '/flyers/eid-ul-adha.png',
    category: 'Eid Prayer',
  },
  {
    id: 'midterm-exams',
    title: 'Midterms Examination',
    description: 'Students will be assessed on their understanding of the material covered so far this semester. Midterms are worth 50% of your final grade.',
    date: 'May 16th - 17th, 2026',
    time: '3:00 PM - 5:00 PM',
    location: 'St. Paul Catholic School, 54 St. Paul Crescent, Barrie, ON',
    image: '/flyers/midterm-exams.png',
    category: 'Education',
  },
  {
    id: 'community-bbq',
    title: 'TNMC Community BBQ',
    description: 'Good food, great people, stronger community! Fun activities including bouncing castle, face painting, cotton candy, popcorn machines, and more!',
    date: 'Sunday, May 31st, 2026',
    time: '3:00 PM - 7:00 PM',
    location: 'St. Paul Parking Lot, 54 St. Paul Crescent, Barrie, ON',
    image: '/flyers/community-bbq.png',
    category: 'Community Event',
  },
  {
    id: 'eid-bbq-market-vendors',
    title: 'Eid BBQ & Market - Vendor Spots',
    description: 'Showcase and sell your products & services! Reserve your vendor table for $50 and grow your business locally. Perfect for handmade goods, fashion, food & beverages, and more!',
    date: 'Sunday, May 31st, 2026',
    time: '3:00 PM - 7:00 PM',
    location: 'St. Paul, 54 St. Paul Crescent, Barrie, ON',
    image: '/flyers/eid-bbq-market.png',
    category: 'Market',
  },
  {
    id: 'eid-bbq-sponsorship',
    title: 'Eid BBQ & Market 2026 - Sponsorship',
    description: 'Become a sponsor! Gold ($500), Silver ($250), or Bronze ($100) packages available with various benefits including logo placement, social media promotion, and booth at the event.',
    date: 'Sunday, May 31st, 2026',
    time: '3:00 PM - 7:00 PM',
    location: 'St. Paul, 54 St. Paul Crescent, Barrie, ON',
    image: '/flyers/eid-bbq-sponsorship.png',
    category: 'Sponsorship',
  },
];

const FlyersPage = () => {
  const [flyers, setFlyers] = useState(defaultFlyers);

  useEffect(() => {
    axios.get(`${API}/content/flyers`)
      .then((res) => {
        if (Array.isArray(res.data) && res.data.length > 0) {
          setFlyers(res.data);
        }
      })
      .catch(() => {/* fall back */});
  }, []);

  return (
    <div className="min-h-screen pt-24">
      {/* Hero */}
      <section className="py-16 lg:py-24 hero-gradient islamic-pattern">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-3xl"
          >
            <span className="inline-block px-4 py-1 rounded-full bg-primary/10 text-primary text-sm font-medium mb-4">
              Stay Updated
            </span>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-foreground font-['Outfit'] leading-tight mb-6">
              Community <span className="text-gradient">Flyers</span>
            </h1>
            <p className="text-lg text-muted-foreground leading-relaxed">
              Browse our upcoming events, programs, and community announcements.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Quick Donate Banner */}
      <section className="py-6 bg-gradient-to-r from-primary to-primary/80">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-3 text-white">
              <Heart className="h-6 w-6" />
              <span className="font-semibold text-lg">Support Our Community</span>
            </div>
            <Link to="/donate" data-testid="quick-donate-btn">
              <Button className="bg-white text-primary hover:bg-white/90 rounded-full px-8 font-semibold">
                Donate Now
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Featured Eid ul Adha Flyer */}
      {flyers.filter(f => f.featured).map((flyer) => (
        <section key={flyer.id} className="py-12 lg:py-16 bg-gradient-to-b from-amber-900/20 to-background" data-testid="featured-flyer">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              className="relative"
            >
              <div className="text-center mb-8">
                <span className="inline-block px-6 py-2 rounded-full bg-amber-500/20 text-amber-400 text-lg font-semibold mb-4 border border-amber-500/30">
                  Featured Event
                </span>
                <h2 className="text-3xl md:text-4xl font-bold text-foreground font-['Outfit']">
                  {flyer.title}
                </h2>
              </div>
              
              <div className="max-w-4xl mx-auto">
                <div className="bg-card rounded-3xl border-2 border-amber-500/30 overflow-hidden shadow-2xl shadow-amber-500/10">
                  {/* Flyer Image */}
                  <div className="relative">
                    <img
                      src={flyer.image}
                      alt={flyer.title}
                      className="w-full h-auto"
                    />
                  </div>

                  {/* Info */}
                  <div className="p-6 md:p-8 bg-gradient-to-t from-card to-transparent">
                    <p className="text-muted-foreground mb-4">{flyer.description}</p>
                    
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                      <div className="flex items-center gap-2 text-amber-400">
                        <Calendar className="h-5 w-5" />
                        <span className="font-medium">{flyer.date}</span>
                      </div>
                      <div className="flex items-center gap-2 text-amber-400">
                        <Clock className="h-5 w-5" />
                        <span className="font-medium">{flyer.time}</span>
                      </div>
                      <div className="flex items-center gap-2 text-amber-400">
                        <MapPin className="h-5 w-5" />
                        <span className="font-medium">{flyer.location}</span>
                      </div>
                    </div>

                    <div className="mt-6 flex flex-wrap gap-4">
                      <a 
                        href={flyer.image} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-amber-500 text-white hover:bg-amber-600 font-semibold transition-colors"
                      >
                        <ExternalLink className="h-5 w-5" />
                        View Full Flyer
                      </a>
                      <Link to={flyer.id === 'place-to-worship' ? '/place-to-worship' : '/donate'}>
                        <Button variant="outline" className="rounded-full px-6 border-amber-500/50 text-amber-400 hover:bg-amber-500/10">
                          <Heart className="mr-2 h-4 w-4" />
                          {flyer.id === 'place-to-worship' ? 'Learn More & Contribute' : 'Donate for Eid'}
                        </Button>
                      </Link>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </section>
      ))}

      {/* Flyers Grid */}
      <section className="py-16 lg:py-24" data-testid="flyers-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHeading
            badge="More Events"
            title="Events & Announcements"
            description="Check out what's happening in our community"
          />

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {flyers.filter(f => !f.featured).map((flyer, index) => (
              <motion.div
                key={flyer.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-card rounded-2xl border border-border overflow-hidden hover:border-primary/50 transition-all"
                data-testid={`flyer-${flyer.id}`}
              >
                {/* Flyer Image */}
                <div className="relative aspect-square overflow-hidden bg-muted">
                  <img
                    src={flyer.image}
                    alt={flyer.title}
                    className="w-full h-full object-contain"
                  />
                  <div className="absolute top-4 left-4">
                    <span className="px-3 py-1 rounded-full bg-primary text-primary-foreground text-xs font-medium">
                      {flyer.category}
                    </span>
                  </div>
                </div>

                {/* Flyer Info */}
                <div className="p-6 space-y-4">
                  <h3 className="text-xl font-semibold text-foreground font-['Outfit']">{flyer.title}</h3>
                  <p className="text-muted-foreground text-sm">{flyer.description}</p>
                  
                  <div className="space-y-2 text-sm">
                    <div className="flex items-center gap-2 text-muted-foreground">
                      <Calendar className="h-4 w-4 text-primary" />
                      <span>{flyer.date}</span>
                    </div>
                    <div className="flex items-center gap-2 text-muted-foreground">
                      <Clock className="h-4 w-4 text-primary" />
                      <span>{flyer.time}</span>
                    </div>
                    <div className="flex items-center gap-2 text-muted-foreground">
                      <MapPin className="h-4 w-4 text-primary" />
                      <span>{flyer.location}</span>
                    </div>
                  </div>

                  {/* View Full Size Link */}
                  <a 
                    href={flyer.image} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 text-primary hover:underline text-sm font-medium"
                  >
                    <ExternalLink className="h-4 w-4" />
                    View Full Flyer
                  </a>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact CTA */}
      <section className="py-16 lg:py-24 bg-card border-t border-border">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl md:text-3xl font-bold font-['Outfit'] text-foreground mb-4">
            Want to Share a Flyer?
          </h2>
          <p className="text-muted-foreground mb-8">
            Have an event or announcement for the community? Contact us to get it featured!
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link to="/contact">
              <Button className="rounded-full px-8">
                Contact Us
              </Button>
            </Link>
            <Link to="/donate">
              <Button variant="outline" className="rounded-full px-8">
                <Heart className="mr-2 h-4 w-4" />
                Support TNMC
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default FlyersPage;
